import React, { useEffect, useState, useRef } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Autocomplete,
} from "@mui/material";
import SpasTable from "../../../components/common/Tables/SpasTable";
import { useSelector, useDispatch } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { useSearchParams } from "react-router-dom";
import { SpaErrorLabels } from "../../../mqttData/errors/spaErrorLabels.enums";
import { ArcticLabels } from "../../SpaErrors/components/arcticLabels.enums";
import { formatErrorCode } from "../../SpaDetails/formatCodes";
import { fetchDealerships } from "../../../redux/actions/dealership.actions";
import { AppDispatch } from "../../../redux/store";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { shortPalName } from "../../../utils/brand";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChipIcon,
  CloseIcon,
  DownloadLineIcon,
  FilterLineIcon,
  TerminalLineIcon,
} from "../../../components/common/Icons/Icons";

interface ContainerProps {
    checked: any[];
    setChecked: any;
    setConfirmUpdate: any;
    setConfirmLogs: any;
    setDeleteOpen: any;
    setEditOpen:any;
    setAddCommandsOpen: any;
    setSpaToModify:any;
}

// URL params the filter panel owns (counted on the Filters button)
const filterParams = [
  'guid', 'nick_name', 'arctic_serial_number', 'ip_address', 'user_email', 'user_name', 'dealership',
  'online', 'has_internet', 'has_errors', 'error', 'registered', 'is_moved',
  'yocto_min', 'yocto_max', 'lpc_min', 'lpc_max', 'sb_min', 'sb_max',
];

const SpasContainer: React.FC<ContainerProps> = ({
    checked,
    setChecked,
    setConfirmUpdate,
    setConfirmLogs,
    setDeleteOpen,
    setEditOpen,
    setAddCommandsOpen,
    setSpaToModify,
}) => {
  const {data: firmware} = useSelector((state: RootState) => state.firmware);
  const {canAccess:canUpdateFirmware} = usePermissionCheck(['can_upgrade_firmware']) ;
  const {canAccess:canRequestLogs} = usePermissionCheck(['request_spa_logs']) ;
  const {canAccess:canSendApiCommands} = usePermissionCheck(['assign_spa_api_commands']) ;
  const {canAccess:canAccessDealerships} = usePermissionCheck(['view_dealerships']) ;
  const dispatch = useDispatch<AppDispatch>();
  const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector((state: RootState) => state.dealerships);
  const [searchParams, setSearchParams] = useSearchParams();

  // Open by default: every filter in view, nothing tucked away
  const [showFilters, setShowFilters] = useState(true);

  // Search and Filter
  const [guid, setGuid] = useState("");
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [ip, setIp] = useState("");
  const [email, setEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [online, setOnline] = useState(false);
  const [hasInternet, setHasInternet] = useState(false);
  const [registered, setRegistered] = useState<boolean | null>(null); // null for all, true for registered, false for unregistered
  const [hasErrors, setHasErrors] = useState(false);
  const [error, setError] = useState("");
  const [isMoved, setIsMoved] = useState<boolean | null>(null);

  const [yoctoMin, setYoctoMin] = useState("");
  const [yoctoMax, setYoctoMax] = useState("");
  const [sbMin, setSBMin] = useState("");
  const [sbMax, setSBMax] = useState("");
  const [lpcMin, setLPCMin] = useState("");
  const [lpcMax, setLPCMax] = useState("");

  const [dealershipInput, setDealershipInput] = useState("");
  const [selectedDealershipId, setSelectedDealershipId] = useState<string | null>(null);
  const [dealershipPage, setDealershipPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch dealerships when input or page changes
  useEffect(() => {
    if (canAccessDealerships && dropdownOpen) {
      const query = `name=${encodeURIComponent(dealershipInput)}&page=${dealershipPage}`;
      dispatch(fetchDealerships(query));
    }
  }, [dealershipInput, dealershipPage, dropdownOpen, canAccessDealerships, dispatch]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.dealership-dropdown')
      ) {
        setDropdownOpen(false);
      }
    }
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    setGuid(searchParams.get('guid')??'');
    setName(searchParams.get('nick_name')??'');
    setSerial(searchParams.get('arctic_serial_number')??'');
    setIp(searchParams.get('ip_address')??'');
    setEmail(searchParams.get('user_email')??'');
    setUserName(searchParams.get('user_name')??'');
    setError(searchParams.get('error')??'');
    setHasErrors(searchParams.get('has_errors')!==null);
    setOnline(searchParams.get('online')!==null);
    setHasInternet(searchParams.get('has_internet')!==null);
    setRegistered(searchParams.get('registered')!==null ? searchParams.get('registered') === 'true' : null);
    const isMovedParam = searchParams.get('is_moved');
    setIsMoved(isMovedParam !== null ? isMovedParam === 'true' : null);

    setYoctoMin(searchParams.get('yocto_min')??'');
    setYoctoMax(searchParams.get('yocto_max')??'');
    setSBMin(searchParams.get('sb_min')??'');
    setSBMax(searchParams.get('sb_max')??'');
    setLPCMin(searchParams.get('lpc_min')??'');
    setLPCMax(searchParams.get('lpc_max')??'');

    setDealershipPage(1);
  }, [searchParams]);

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    guid==="" ? searchParams.delete('guid') : searchParams.set('guid', guid);
    name==="" ? searchParams.delete('nick_name') : searchParams.set('nick_name', name);
    serial==="" ? searchParams.delete('arctic_serial_number') : searchParams.set('arctic_serial_number', serial);
    ip==="" ? searchParams.delete('ip_address') : searchParams.set('ip_address', ip);
    email==="" ? searchParams.delete('user_email') : searchParams.set('user_email', email);
    userName==="" ? searchParams.delete('user_name') : searchParams.set('user_name', userName);
    error==="" ? searchParams.delete('error') : searchParams.set('error', error);

    !online ? searchParams.delete('online') : searchParams.set('online', 'true');
    !hasInternet ? searchParams.delete('has_internet') : searchParams.set('has_internet', 'true');
    registered === null ? searchParams.delete('registered') : searchParams.set('registered', registered.toString());
    !hasErrors ? searchParams.delete('has_errors') : searchParams.set('has_errors', 'true');
    isMoved === null ? searchParams.delete('is_moved') : searchParams.set('is_moved', isMoved.toString());

    yoctoMin==="" ? searchParams.delete('yocto_min') : searchParams.set('yocto_min', yoctoMin);
    yoctoMax==="" ? searchParams.delete('yocto_max') : searchParams.set('yocto_max', yoctoMax);
    sbMin==="" ? searchParams.delete('sb_min') : searchParams.set('sb_min', sbMin);
    sbMax==="" ? searchParams.delete('sb_max') : searchParams.set('sb_max', sbMax);
    lpcMin==="" ? searchParams.delete('lpc_min') : searchParams.set('lpc_min', lpcMin);
    lpcMax==="" ? searchParams.delete('lpc_max') : searchParams.set('lpc_max', lpcMax);

    if (selectedDealershipId) {
      searchParams.set('dealership', selectedDealershipId);
    } else {
      searchParams.delete('dealership');
    }

    setSearchParams(searchParams);

    setChecked([]);
  }

  const handleClear = () => {
    setGuid("");
    searchParams.delete('guid');

    setName("");
    searchParams.delete('nick_name');

    setSerial("");
    searchParams.delete('arctic_serial_number');

    setIp("");
    searchParams.delete('ip_address');

    setEmail("");
    searchParams.delete('user_email');

    setUserName("");
    searchParams.delete('user_name');

    setOnline(false);
    searchParams.delete('online');

    setHasInternet(false);
    searchParams.delete('has_internet');

    setRegistered(null);
    searchParams.delete('registered');

    setHasErrors(false);
    searchParams.delete('has_errors');

    setError("");
    searchParams.delete('error');

    setIsMoved(null);
    searchParams.delete('is_moved');

    setYoctoMin("");
    searchParams.delete('yocto_min');
    setYoctoMax("");
    searchParams.delete('yocto_max');

    setLPCMin("");
    searchParams.delete('lpc_min');
    setLPCMax("");
    searchParams.delete('lpc_max');

    setSBMin("");
    searchParams.delete('sb_min');
    setSBMax("");
    searchParams.delete('sb_max');

    setSelectedDealershipId(null);
    setDealershipInput("");
    setDealershipPage(1);
    searchParams.delete('dealership');

    setChecked([]);
    setSearchParams(searchParams);
  }

  const getOnlineSelectedSpas = () => {
    return [...checked].filter((spa:any)=>spa?.is_connected && spa?.is_moved);
  }

  const activeFilters = filterParams.filter((param)=>searchParams.has(param)).length;
  const onlineSelected = getOnlineSelectedSpas().length;
  const hasBulkActions = canUpdateFirmware || canRequestLogs || canSendApiCommands;

  // ---- Field builders ----
  const searchField = (id: string, label: string, value: string, setValue: (v: string) => void, autoComplete: string, className?: string) => (
    <TextField
      id={id}
      className={className}
      label={label}
      type="search"
      fullWidth={true}
      value={value??""}
      onChange={(e)=>setValue(e.target.value)}
      onKeyDown={(e)=>(e.code==="Enter" ? handleFilter() : null)}
      size="small"
      autoComplete={autoComplete}
      inputProps={{ maxLength: 1990 }}
    />
  );

  const toggle = (label: string, value: boolean, onChange: (v: boolean) => void) => (
    <button type="button" className={`lp-toggle ${value ? 'on' : ''}`} aria-pressed={value} onClick={()=>onChange(!value)}>
      <span className="lp-toggle-box" aria-hidden="true"/>
      {label}
    </button>
  );

  // Three-way choice (All / yes / no) on a segmented control; the options name the filter, so no label
  const triState = (label: string, value: boolean | null, setValue: (v: boolean | null) => void, yes: string, no: string) => (
    <div className="segmented lp-segmented" role="group" aria-label={label}>
      {([[null, 'All'], [true, yes], [false, no]] as const).map(([option, text])=>(
        <button key={text} type="button" className={value === option ? 'active' : ''} aria-pressed={value === option} onClick={()=>setValue(option)}>
          {text}
        </button>
      ))}
    </div>
  );

  const versionOptions = (type: string, min: string, max: string) => firmware
    .filter((f: any) => f.type === type && (min !== '' ? f.version >= min : true) && (max !== '' ? f.version <= max : true))
    .map((f: any) => f.version);

  const versionField = (id: string, label: string, options: string[], value: string, setValue: (v: string) => void) => (
    <Autocomplete
      id={id}
      freeSolo
      options={options}
      value={value}
      onInputChange={(event, newValue) => setValue(newValue??"")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
        />
      )}
    />
  );

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={()=>setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      {hasBulkActions && <>
        <span className="lp-divider" aria-hidden="true"/>

        {checked.length > 0 ? (
          <span className="lp-selection">
            {checked.length} selected
            <button type="button" className="lp-clear-selection" onClick={()=>setChecked([])} aria-label="Clear selection">
              <CloseIcon/>
            </button>
          </span>
        ) : (
          <span className="lp-hint">Select spas for bulk actions</span>
        )}

        <div className="lp-bulk">
          {canUpdateFirmware && <button
            type="button"
            className="btn btn-sm dt-tool-btn"
            disabled={onlineSelected===0}
            onClick={()=>setConfirmUpdate(true)}
          >
            <ChipIcon/> Update Firmware
            {onlineSelected>0 && <span className="lp-count">{onlineSelected}</span>}
          </button>}

          {canRequestLogs && <button
            type="button"
            className="btn btn-sm dt-tool-btn"
            disabled={onlineSelected===0}
            onClick={()=>setConfirmLogs(true)}
          >
            <DownloadLineIcon/> Request Logs
            {onlineSelected>0 && <span className="lp-count">{onlineSelected}</span>}
          </button>}

          {canSendApiCommands && <button
            type="button"
            className="btn btn-sm dt-tool-btn"
            disabled={checked.length===0}
            onClick={()=>setAddCommandsOpen(true)}
          >
            <TerminalLineIcon/> Send API Commands
            {checked.length>0 && <span className="lp-count">{checked.length}</span>}
          </button>}
        </div>
      </>}
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group lp-search">
              {/* Full rows: both take long values (several serials at once, 36-character GUIDs) */}
              {searchField("serial-search", "Serial Number", serial, setSerial, "spa-serial-number", "lp-wide")}
              {searchField("guid-search", "Guid", guid, setGuid, "spa-guid", "lp-wide")}
              {searchField("name-search", "Nickname", name, setName, "spa-nickname")}
              {searchField("ip-search", "IP Address", ip, setIp, "spa-ip-address")}
              {searchField("email-search", "Email Address", email, setEmail, "spa-email-address")}
              {searchField("user-name-search", "Name", userName, setUserName, "spa-user-name")}

              {canAccessDealerships && (
                <div className="sp-dealer lp-wide">
                  <TextField
                    inputRef={inputRef}
                    label="Dealership"
                    variant="outlined"
                    size="small"
                    value={dealershipInput}
                    onFocus={() => setDropdownOpen(true)}
                    onChange={e => {
                      const value = e.target.value;
                      setDealershipInput(value);
                      setDealershipPage(1);

                      // If user clears the input, clear the selected dealership and close dropdown
                      if (value === "") {
                        setSelectedDealershipId(null);
                        setDropdownOpen(false);
                      } else {
                        setDropdownOpen(true);
                      }
                    }}
                    autoComplete="off"
                    fullWidth
                  />
                  {dropdownOpen && (
                    <div className="dealership-dropdown sp-dealer-menu">
                      {dealershipsLoading ? (
                        <div className="sp-dealer-state">Loading...</div>
                      ) : (
                        <>
                          {(dealerships || []).filter(d => d && d.id && d.name).length === 0 ? (
                            <div className="sp-dealer-state">No dealerships found</div>
                          ) : (
                            <div className="sp-dealer-list">
                              {(dealerships || []).filter(d => d && d.id && d.name).map((d: any) => (
                                <button
                                  type="button"
                                  key={d.id}
                                  className={`sp-dealer-option ${selectedDealershipId === d.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setDealershipInput(d.name);
                                    setSelectedDealershipId(d.id);
                                    setDropdownOpen(false);
                                  }}
                                >
                                  {d.name}
                                </button>
                              ))}
                            </div>
                          )}
                          <div className="sp-dealer-pager">
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              disabled={dealershipPage <= 1}
                              onClick={() => dealershipPage > 1 && setDealershipPage(dealershipPage - 1)}
                            >
                              <ChevronLeftIcon/> Previous
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-ghost"
                              disabled={!(dealershipsMeta && dealershipPage < dealershipsMeta.total_pages)}
                              onClick={() => dealershipsMeta && dealershipPage < dealershipsMeta.total_pages && setDealershipPage(dealershipPage + 1)}
                            >
                              Next <ChevronRightIcon/>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="lp-group">
              <div className="lp-toggles">
                {toggle("Online", online, setOnline)}
                {toggle("Internet", hasInternet, setHasInternet)}
                {/* The error picker only applies with "Has Errors", so turning that off clears it */}
                {toggle("Has Errors", hasErrors, (value)=>{ setHasErrors(value); if (!value) setError(""); })}
              </div>

              {hasErrors && (
                <FormControl fullWidth size="small">
                  <InputLabel id="error-select-label">Error</InputLabel>
                  <Select
                    labelId="error-select-label"
                    label="Error"
                    onChange={(e)=>setError(e.target.value)}
                    value={error}
                  >
                    <MenuItem value="">All Errors</MenuItem>
                    {Object.values(SpaErrorLabels).map((code,i)=>
                      ArcticLabels[code]!=="" &&
                        <MenuItem key={i} value={code}>{formatErrorCode(code)} {ArcticLabels[code]}</MenuItem>
                      )
                    }
                  </Select>
                </FormControl>
              )}

              {triState("Registration", registered, setRegistered, "Registered", "Unregistered")}
              {triState("Moved Status", isMoved, setIsMoved, "Moved", "Not Moved")}
            </div>

            <div className="lp-group">
              <div className="lp-range">
                {versionField("yocto-selection-min", "Yocto Min", versionOptions('yocto', '', yoctoMax), yoctoMin, setYoctoMin)}
                {versionField("yocto-selection-max", "Yocto Max", versionOptions('yocto', yoctoMin, ''), yoctoMax, setYoctoMax)}
              </div>
              <div className="lp-range">
                {versionField("lpc-selection-min", "LPC Min", versionOptions('lpc', '', lpcMax), lpcMin, setLPCMin)}
                {versionField("lpc-selection-max", "LPC Max", versionOptions('lpc', lpcMin, ''), lpcMax, setLPCMax)}
              </div>
              <div className="lp-range">
                {versionField("spaboy-selection-min", `${shortPalName} Min`, versionOptions('spaboy', '', sbMax), sbMin, setSBMin)}
                {versionField("spaboy-selection-max", `${shortPalName} Max`, versionOptions('spaboy', sbMin, ''), sbMax, setSBMax)}
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <SpasTable
        setChecked={setChecked}
        checked={checked}
        setDeleteOpen={setDeleteOpen}
        setEditOpen={setEditOpen}
        setSpaToModify={setSpaToModify}
        toolbar={toolbar}
      />
    </div>
)
};

export default SpasContainer;
